/**
 * When the chat asks "How was this answer?".
 *
 * The question is meant to be rare, so it never becomes noise:
 * - only under the latest answer, once it has finished;
 * - at most once per conversation, and it stays only while that answer is fresh;
 * - at most once every 8 hours across all conversations;
 * - not at all for a week after "Not now".
 *
 * Every answer shows the Bad / Fine / Good choices anyway, so a question that
 * is not shown never stops anyone from giving feedback. Which answers are rated comes from
 * the server (`LogResponse.FeedbackRating`); only the asking rhythm is kept in
 * localStorage, and losing it only means the question may come back sooner.
 */

export type FeedbackRating = 1 | 2 | 3;

export const FEEDBACK_RATINGS: readonly { value: FeedbackRating; label: string }[] = [
  { value: 1, label: "Bad" },
  { value: 2, label: "Fine" },
  { value: 3, label: "Good" },
];

export function feedbackLabel(rating: FeedbackRating): string {
  return FEEDBACK_RATINGS.find((r) => r.value === rating)?.label ?? "";
}

export function isFeedbackRating(value: unknown): value is FeedbackRating {
  return value === 1 || value === 2 || value === 3;
}

/** The longest comment the server stores. */
export const FEEDBACK_COMMENT_MAX = 1000;

const HOUR = 60 * 60 * 1000;
/** Gap between two questions in different conversations. */
export const ASK_COOLDOWN_MS = 8 * HOUR;
/** "Not now" silences the question this long. */
export const SNOOZE_MS = 7 * 24 * HOUR;
/** The question stays on its answer this long; reopening the conversation later does not repeat it. */
export const ASK_VISIBLE_MS = 30 * 60 * 1000;
/** Conversations remembered as asked; the oldest are dropped first. */
const MAX_REMEMBERED = 200;

export interface FeedbackAskState {
  /** Conversation GUID -> the answer the question was shown under, and when. */
  asked: Record<string, { logId: number; at: number }>;
  /** When the question was last shown in any conversation (epoch ms). */
  lastAskAt: number;
  /** No question before this time (epoch ms). */
  snoozedUntil: number;
}

export function emptyFeedbackAskState(): FeedbackAskState {
  return { asked: {}, lastAskAt: 0, snoozedUntil: 0 };
}

export interface FeedbackAskInput {
  conversationGuid: string;
  /** The latest, finished answer. */
  logId: number;
  /** True when any answer in the conversation is already rated. */
  conversationRated: boolean;
  now: number;
}

/**
 * Whether to ask under this answer, and the state to store when the answer is
 * yes. Calling it again for the same answer gives the same result, so a
 * re-render or StrictMode's double effect does not use up the question.
 */
export function decideFeedbackAsk(
  state: FeedbackAskState,
  { conversationGuid, logId, conversationRated, now }: FeedbackAskInput,
): { ask: boolean; state: FeedbackAskState } {
  if (conversationRated || !conversationGuid) return { ask: false, state };

  const previous = state.asked[conversationGuid];
  if (previous) {
    // Already asked here: keep the question on that answer while it is fresh,
    // and never move it to a later one.
    return { ask: previous.logId === logId && now - previous.at < ASK_VISIBLE_MS, state };
  }
  if (now < state.snoozedUntil) return { ask: false, state };
  if (now - state.lastAskAt < ASK_COOLDOWN_MS) return { ask: false, state };

  const asked = { ...state.asked, [conversationGuid]: { logId, at: now } };
  const keys = Object.keys(asked);
  if (keys.length > MAX_REMEMBERED) {
    keys.sort((a, b) => asked[a]!.at - asked[b]!.at);
    for (const key of keys.slice(0, keys.length - MAX_REMEMBERED)) delete asked[key];
  }
  return { ask: true, state: { ...state, asked, lastAskAt: now } };
}

/** "Not now": no question anywhere for a week. */
export function snoozeFeedbackAsk(state: FeedbackAskState, now: number): FeedbackAskState {
  return { ...state, snoozedUntil: now + SNOOZE_MS };
}

const STORAGE_KEY = "issaFeedbackAsk";

/** Best effort: localStorage throws in private mode and with blocked storage. */
export function readFeedbackAskState(): FeedbackAskState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyFeedbackAskState();
    const parsed = JSON.parse(raw) as Partial<FeedbackAskState> | null;
    return {
      asked: parsed?.asked && typeof parsed.asked === "object" ? parsed.asked : {},
      lastAskAt: typeof parsed?.lastAskAt === "number" ? parsed.lastAskAt : 0,
      snoozedUntil: typeof parsed?.snoozedUntil === "number" ? parsed.snoozedUntil : 0,
    };
  } catch {
    return emptyFeedbackAskState();
  }
}

export function writeFeedbackAskState(state: FeedbackAskState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable: the question may simply come back sooner */
  }
}

/** Ids of answers saved on the server. A message still on its way has a temporary id (Date.now()). */
export function isSavedAnswerId(id: number): boolean {
  return Number.isInteger(id) && id > 0 && id <= 2_147_483_647;
}
